import { BOARD_EVENT_PHRASE, boardEventTone, formatCard, formatCards } from '../cards';
/** How each equity-delta bucket reads, with its tone (schema/buckets.ts DeltaBucket). */
const DELTA_PHRASE = {
    collapsed: { verb: 'collapsed', tone: 'bad' },
    slipped: { verb: 'slipped', tone: 'caution' },
    held: { verb: 'held', tone: 'neutral' },
    improved: { verb: 'improved', tone: 'good' },
    surged: { verb: 'surged', tone: 'good' },
};
/** The card(s) newly revealed on this street. Flop shows all three; turn/river the single new card.
 *  Returns [] preflop (no board) - whatChanged is a post-preflop feature (§8.2). */
function newCardsFor(facts) {
    const board = facts.board.cards;
    switch (facts.street) {
        case 'flop':
            return board.slice(0, 3);
        case 'turn':
            return board.length >= 4 ? [board[3]] : [];
        case 'river':
            return board.length >= 5 ? [board[4]] : [];
        default:
            return [];
    }
}
/**
 * What changed since the previous street: the new board card(s) + event, the equity swing, the range-
 * advantage shift. Empty preflop and whenever there is no cross-street delta to narrate (§8.2: what-
 * changed is street > preflop only). Reads the STRUCTURED whatChanged block + hero.equityDelta; the
 * engine's own whatChanged.lines are not echoed - the coach owns its copy.
 */
export function whatChanged(facts) {
    if (facts.street === 'preflop')
        return [];
    const lines = [];
    // 1. The board card(s) that just fell, with the classified event.
    const newCards = newCardsFor(facts);
    const ev = facts.board.streetEvent;
    const streetCap = facts.street.charAt(0).toUpperCase() + facts.street.slice(1);
    if (newCards.length > 0) {
        if (facts.street === 'flop') {
            lines.push({ kind: 'what_changed', text: `Flop: ${formatCards(newCards)}.`, tone: 'neutral' });
        }
        else if (ev) {
            const extras = [];
            if (ev.completesFlush)
                extras.push('completes a flush');
            if (ev.pairsBoard)
                extras.push('pairs the board');
            const suffix = extras.length ? ` (${extras.join(', ')})` : '';
            lines.push({
                kind: 'what_changed',
                text: `${streetCap} ${formatCard(newCards[0])}, ${BOARD_EVENT_PHRASE[ev.kind]}${suffix}.`,
                tone: boardEventTone(ev),
            });
        }
        else {
            lines.push({
                kind: 'what_changed',
                text: `${streetCap} ${formatCard(newCards[0])}.`,
                tone: 'neutral',
            });
        }
    }
    // 2. The equity swing (exact points when the engine recorded them, else the bucket verb).
    const bucket = facts.hero.equityDeltaBucket ?? facts.whatChanged?.equityDeltaBucket;
    if (bucket) {
        const { verb, tone } = DELTA_PHRASE[bucket];
        const delta = facts.hero.equityDelta;
        const pts = delta === undefined ? '' : ` (${delta >= 0 ? '+' : ''}${Math.round(delta)} pts)`;
        lines.push({ kind: 'what_changed', text: `Your equity ${verb}${pts}.`, tone });
    }
    // 3. The range-advantage shift, when the engine flagged one.
    const shift = facts.whatChanged?.rangeAdvantageShift;
    if (shift === 'toHero') {
        lines.push({ kind: 'what_changed', text: 'Range advantage shifted toward you.', tone: 'good' });
    }
    else if (shift === 'toVillain') {
        lines.push({
            kind: 'what_changed',
            text: 'Range advantage shifted toward the villain.',
            tone: 'caution',
        });
    }
    return lines;
}
//# sourceMappingURL=whatChanged.js.map